import { IconFont } from '@/components';
import { borderColor } from '@/styles/variants';
import { Button } from '@arco-design/web-react';
import { IconDelete, IconEdit } from '@arco-design/web-react/icon';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { GrayBox } from '../../tools';
import {
  isVolumeClaimTemplateItem,
  isVolumeKeymapItem,
  isVolumeStorageItem,
  VolumeClaimTemplateItem,
  VolumeItem,
  VolumeKeymapItem,
  VolumeKeymapTab,
  VolumeMountsProps,
  VolumeMountTab,
  VolumeMountType,
  VolumeStorageItem,
} from '../_hooks';
import { VolumeOptionLabel } from './volume-option-label';

interface VolumeItemAction {
  onEdit?: (item: VolumeItem, index: number) => void;
  onDelete?: (item: VolumeItem) => void;
}

interface StorageItemsProps extends VolumeItemAction {
  items: VolumeItem[];
  disableEditVolumeClaimTemplate?: boolean; // 是否禁用编辑按钮
}

const getMsg = (t: any) => ({
  storage_class: t('common:storage.class'),
  volume_type: t('common:volume.type'),
  persistent_volume_claim: t('common:persistent.volume.claim'),
  temp_volume: t('common:ephemeral.volume'),
  host_path: 'HostPath',
  config_map: t('common:configmap'),
  secret_map: t('common:secret'),
  read_only: t('common:read.only'),
  read_write: t('common:read.write'),
  capacity: t('common:capacity'),
  access_modes: t('common:access.mode'),
});

/** 挂载卷的展示列表 */
export function StorageItems({
  items,
  onEdit,
  onDelete,
  disableEditVolumeClaimTemplate,
}: StorageItemsProps) {
  const { t } = useTranslation();
  const msg = getMsg(t);

  if (isEmpty(items) || !Array.isArray(items)) return null;
  return (
    <Box>
      {items.map((item, index) => (
        <div key={index}>
          {isVolumeClaimTemplateItem(item) && (
            <VolumeClaimTemplateCard
              index={index}
              item={item as VolumeClaimTemplateItem}
              onEdit={onEdit}
              onDelete={onDelete}
              disableEdit={disableEditVolumeClaimTemplate}
              msg={msg}
            />
          )}
          {isVolumeStorageItem(item) && (
            <VolumeItemCard
              index={index}
              item={item as VolumeStorageItem}
              onEdit={onEdit}
              onDelete={onDelete}
              msg={msg}
            />
          )}
          {isVolumeKeymapItem(item) && (
            <KeymapItemCard
              index={index}
              item={item as VolumeKeymapItem}
              onEdit={onEdit}
              onDelete={onDelete}
              msg={msg}
            />
          )}
        </div>
      ))}
    </Box>
  );
}

/** ============================ VolumeClaimTemplateCard ==================================== */
interface VolumeClaimTemplateCardProps extends VolumeItemAction {
  item: VolumeClaimTemplateItem;
  index: number;
  // 是否禁用编辑按钮
  disableEdit?: boolean;
  msg: ReturnType<typeof getMsg>;
}
function VolumeClaimTemplateCard({
  item,
  index,
  onEdit,
  onDelete,
  disableEdit,
  msg,
}: VolumeClaimTemplateCardProps) {
  return (
    <>
      <VolumeItemLayout>
        <VolumeItemBox>
          <VolumeOptionLabel
            iconfontName="icon-chijiujuanshengming"
            value={item.volumeName}
            label={`${msg.storage_class}: ${item.storageClass}`}
            extraItems={[
              { label: msg.capacity, value: `${item.volumeCapacity}Gi`, frozenWidth: true },
              {
                label: msg.access_modes,
                value: `${item.accessModes.join(', ')}`,
                frozenWidth: true,
              },
            ]}
            style={{ flex: 1 }}
          />
          {!disableEdit && (
            <ItemActionBox>
              <Button
                iconOnly
                type="text"
                icon={<IconEdit />}
                onClick={() => onEdit?.(item, index)}
              />
              <Button
                iconOnly
                type="text"
                status="danger"
                icon={<IconDelete />}
                onClick={() => onDelete?.(item)}
              />
            </ItemActionBox>
          )}
        </VolumeItemBox>
        <ContainerMounts items={item.volumeMounts} />
      </VolumeItemLayout>
    </>
  );
}

/** ============================ KeymapItemCard ==================================== */
interface VolumeItemCardProps extends VolumeItemAction {
  item: VolumeKeymapItem;
  index: number;
  msg: ReturnType<typeof getMsg>;
}
/** 挂载配置字典和保密字典展示的卡片 */
function KeymapItemCard({ item, onEdit, onDelete, index, msg }: VolumeItemCardProps) {
  switch (item.type) {
    case VolumeKeymapTab.CONFIG_MAP:
      return (
        <>
          <VolumeItemLayout>
            <VolumeItemBox>
              <VolumeOptionLabel
                iconfontName="icon-a-peizhizidianmian"
                value={item.selected}
                label={`${msg.volume_type}: ${msg.config_map}`}
                extraItems={[]}
              />
              <ItemActionBox>
                <Button
                  iconOnly
                  type="text"
                  icon={<IconEdit />}
                  onClick={() => onEdit?.(item, index)}
                />
                <Button
                  iconOnly
                  type="text"
                  status="danger"
                  icon={<IconDelete />}
                  onClick={() => onDelete?.(item)}
                />
              </ItemActionBox>
            </VolumeItemBox>
            <ContainerMounts items={item.volumeMounts} />
          </VolumeItemLayout>
        </>
      );
    case VolumeKeymapTab.SECRET_MAP:
      return (
        <>
          <VolumeItemLayout>
            <VolumeItemBox>
              <VolumeOptionLabel
                iconfontName="icon-a-baomizidianmian"
                value={item.selected}
                label={`${msg.volume_type}: ${msg.secret_map}`}
                extraItems={[]}
              />
              <ItemActionBox>
                <Button
                  iconOnly
                  type="text"
                  icon={<IconEdit />}
                  onClick={() => onEdit?.(item, index)}
                />
                <Button
                  iconOnly
                  type="text"
                  status="danger"
                  icon={<IconDelete />}
                  onClick={() => onDelete?.(item)}
                />
              </ItemActionBox>
            </VolumeItemBox>
            <ContainerMounts items={item.volumeMounts} />
          </VolumeItemLayout>
        </>
      );
    default:
      return null;
  }
}

/** ============================ VolumeItemCard ==================================== */
interface KeymapItemCardProps extends VolumeItemAction {
  item: VolumeStorageItem;
  index: number;
  msg: ReturnType<typeof getMsg>;
}
/** 挂载卷的展示卡片 */
function VolumeItemCard({ item, onEdit, onDelete, index, msg }: KeymapItemCardProps) {
  let extraItems = [];
  switch (item.type) {
    case VolumeMountTab.PERSISTENCE:
      extraItems = [];
      break;
    case VolumeMountTab.TEMPORARY:
      extraItems = [];
      break;
    case VolumeMountTab.HOST_PATH:
      extraItems = [
        {
          value: item.volumeHostPath,
          label: 'HostPath',
          frozenWidth: 225,
        },
      ];
      break;
    default:
      break;
  }

  return (
    <>
      <VolumeItemLayout>
        <VolumeItemBox>
          <VolumeOptionLabel
            iconfontName="icon-guazaizilujing"
            value={item.volumeName}
            label={msg.volume_type}
            extraItems={extraItems}
            style={{ flex: 1 }}
          />
          <ItemActionBox>
            <Button
              iconOnly
              type="text"
              icon={<IconEdit />}
              onClick={() => onEdit?.(item, index)}
            />
            <Button
              iconOnly
              type="text"
              status="danger"
              icon={<IconDelete />}
              onClick={() => onDelete?.(item)}
            />
          </ItemActionBox>
        </VolumeItemBox>
        <ContainerMounts items={item.volumeMounts} />
      </VolumeItemLayout>
    </>
  );
}

/** ============================ ContainerMounts ==================================== */
/** 挂载的容器的展示项 */
function ContainerMounts({ items }: { items: VolumeMountsProps['volumeMounts'] }) {
  const { t } = useTranslation();
  const msg = getMsg(t);

  return (
    <>
      {items
        ?.filter((item) => item.mountType !== VolumeMountType.NotMount) // 不挂载的不展示
        ?.map((mount, index) => (
          <ContainerBox key={index}>
            <ContainerItem>
              <IconFont name="icon-rongqi" style={{ opacity: 0.45 }} />
              <span>{mount.containerName}</span>
            </ContainerItem>
            <ContainerItem>
              <IconFont name="icon-guazaizilujing" style={{ opacity: 0.45 }} />
              <span>
                {mount.mountPath} (
                {mount.mountType === VolumeMountType.ReadOnly ? msg.read_only : msg.read_write})
              </span>
            </ContainerItem>
          </ContainerBox>
        ))}
    </>
  );
}

/** ============================ 样式 ==================================== */
const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const VolumeItemLayout = styled.div`
  padding: 8px;
  border: 1px solid ${borderColor};
  transition: all 0.3s;
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const VolumeItemBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const ItemActionBox = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ContainerBox = styled(GrayBox)`
  padding-block: 0;
  margin-top: 1rem;
  display: flex;
  gap: 8px;
`;

const ContainerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 200px;
`;
